import { formatPhoneNumber } from "../../../components/WhatsappDropdown/utils/formatPhoneNumber";
import { resolveDisplayUnitId } from "../../unit";
import { formatDate } from "../../formatDate";
import { ContentFieldType } from "../../../types/cms/contentField.type";
import { createTokenRegex } from "../internal/constants";
import { transformRelationsToFlat } from "../internal/flatten";
import { parseToStringList } from "../internal/parseList";
import { resolveConditionals } from "../internal/resolveConditionals";
import type {
  DynamicStructure,
  InterpolateOptions,
  InterpolateParams,
} from "../types";
import { sanitizeToPlainText } from "../internal/sanitizeToPlainText";
import { getValidTags } from "../../tags";
import { formatLowestPlanSummary } from "../../subscription/plans";

type ReferenceLookup = Map<string, Record<string, any>>;

export function buildReferenceLookups({
  dynamicStructure,
}: {
  dynamicStructure: DynamicStructure;
}): Record<string, ReferenceLookup> {
  interface PhoneEntry {
    name?: string;
    phoneNumber?: string | null;
    order?: number;
    children?: PhoneEntry[];
  }

  interface OperatingHourEntry {
    dayOfWeek?: string;
    hours?: string;
  }

  const formatPhonesList = (phones: PhoneEntry[]): string => {
    if (!phones?.length) return "";
    const lines: string[] = [];
    for (const phone of phones) {
      if (phone.children?.length) {
        for (const child of phone.children) {
          const label = phone.name || "";
          const number = child.phoneNumber || "";
          lines.push(label ? `${label}: ${number}` : number);
        }
      } else if (phone.phoneNumber) {
        const label = phone.name || "";
        lines.push(
          label ? `${label}: ${phone.phoneNumber}` : phone.phoneNumber,
        );
      }
    }
    return lines.join("\n");
  };

  const formatOperatingHours = (hours: OperatingHourEntry[]): string => {
    if (!hours?.length) return "";
    return hours
      .map((h) => `${h.dayOfWeek || ""} ${h.hours || ""}`.trim())
      .join(" · ");
  };

  const normalizeUnit = (unit: Record<string, unknown>) => {
    if (!unit || typeof unit !== "object") return unit;

    const sortedPhones = Array.isArray(unit.phones)
      ? ([...unit.phones] as PhoneEntry[]).sort(
          (a, b) => (a.order ?? 0) - (b.order ?? 0),
        )
      : [];

    return {
      ...unit,
      phoneNumber:
        unit.phoneNumber || sortedPhones[0]?.phoneNumber || null,
      phonesList: formatPhonesList(sortedPhones),
      hoursFormatted: formatOperatingHours(
        (unit.operatingHours as OperatingHourEntry[]) || [],
      ),
    };
  };

  return {
    unit_id: new Map(
      (dynamicStructure.channel?.units || []).map((unit: any) => [
        unit.id,
        normalizeUnit(unit),
      ]),
    ),
    brand_id: new Map(
      (dynamicStructure.brands || [])
        .filter((brand) => brand.name)
        .map((brand) => [brand.id, { name: brand.name }]),
    ),
  };
}

/**
 * Interpola tokens `{{path|type}}` num template e retorna **string pura**.
 *
 * Para tipos com payload estruturado (image, gallery, unit_id) em modo
 * single-token, degrada para uma representação textual:
 *  - image  → `value.src` ou ''
 *  - gallery → '' (sem representação textual significativa)
 *  - unit_id (Unit) → para valores unit_id, resolve o objeto Unit
 *
 * Para datetime, retorna o ISO string bruto. Formatação visual é
 * responsabilidade do orquestrador via `interpolate({...}).node`.
 */
export function interpolateString(
  { structure, template }: InterpolateParams,
  options?: InterpolateOptions,
): string {
  const flatStructure = transformRelationsToFlat(structure);
  const lookups = buildReferenceLookups({ dynamicStructure: flatStructure });
  const unitMap = lookups.unit_id;
  const resolvedTemplate = resolveConditionals({ template, structure: flatStructure });

  const result = resolvedTemplate.replace(
    createTokenRegex(),
    (_match, path: string, type: string) => {
      if (type === ContentFieldType.UNIT_ID) {
        const keys = path.split(".");
        let value: any = flatStructure;

        for (let i = 0; i < keys.length; i++) {
          if (value == null || typeof value !== "object") return "";
          value = value[keys[i]];

          if (Array.isArray(value)) {
            value = resolveDisplayUnitId(value);
          }

          if (
            typeof value === "string" &&
            i < keys.length - 1 &&
            unitMap.has(value)
          ) {
            value = unitMap.get(value);
          }
        }

        if (typeof value === "string" && unitMap.has(value)) {
          return "";
        }

        if (value == null) return "";
        if (
          keys[keys.length - 1] === "phoneNumber" &&
          typeof value === "string"
        ) {
          return formatPhoneNumber(value);
        }
        return String(value);
      }

      if (type === ContentFieldType.BRAND_ID) {
        const brandLookup = lookups.brand_id;
        const keys = path.split(".");
        let value: any = flatStructure;

        for (const key of keys) {
          if (value == null || typeof value !== "object") return "";
          value = value[key];
        }

        if (typeof value === "string" && brandLookup.has(value)) {
          const brand = brandLookup.get(value);
          return brand?.name ?? "";
        }

        if (value == null) return "";
        return String(value);
      }

      const keys = path.split(".");
      let value: any = flatStructure;

      for (const key of keys) {
        if (value == null || typeof value !== "object") return "";
        value = value[key];
      }

      if (value == null) return "";

      if (type === ContentFieldType.PRICE) {
        const num = typeof value === "string" ? parseFloat(value) : value;
        if (isNaN(num)) return "";
        return num.toLocaleString("pt-BR", {
          style: "currency",
          currency: "BRL",
        });
      }

      if (
        (type === ContentFieldType.GALLERY ||
          type === ContentFieldType.IMAGE) &&
        typeof value === "object"
      ) {
        return value?.src ?? "";
      }

      if (type === ContentFieldType.MEDIA && typeof value === "object") {
        if (value?.type === "video") {
          return value?.video?.videoUrl ?? "";
        }
        return value?.image?.desktop?.src ?? value?.image?.mobile?.src ?? "";
      }

      if (type === ContentFieldType.ARRAY_STRING) {
        const list = parseToStringList(value);
        return list ? list.join(", ") : "";
      }

      if (type === ContentFieldType.COLORS) {
        if (!Array.isArray(value)) return "";
        return value
          .map((item) =>
            item && typeof item === "object"
              ? (item as { name?: unknown }).name
              : undefined,
          )
          .filter(
            (name): name is string =>
              typeof name === "string" && name.length > 0,
          )
          .join(", ");
      }

      // Compostos sem representação textual: a lista inteira só faz sentido
      // pelo getter dedicado (`.repeater`).
      if (
        type === ContentFieldType.INSTALLMENTS ||
        type === ContentFieldType.REPEATER
      ) {
        return "";
      }

      // Matriz de planos de assinatura: em texto vira "A partir de R$ X/mês"
      // (vazio quando nenhum plano é publicável).
      if (type === ContentFieldType.SUBSCRIPTION_PLANS) {
        return formatLowestPlanSummary(value);
      }

      /* Selos em texto viram a lista de rótulos: é o que faz sentido num
         atributo (alt, title) — cor e ícone não têm representação textual. */
      if (type === ContentFieldType.TAGS) {
        return getValidTags(value)
          .map((tag) => tag.label)
          .join(", ");
      }

      if (type === ContentFieldType.DATE) {
        return formatDate(value instanceof Date ? value : String(value));
      }

      if (value instanceof Date) {
        return value.toISOString();
      }

      return String(value);
    },
  );

  return options?.sanitizeHtml ? sanitizeToPlainText(result) : result;
}
